import { webEnums } from '@vectronic/openApi-customer';

export class Parser {
  static optionalInteger(header: string) {
    return (s: string) => {
      if (s === '') return;
      return this.integer(header)(s);
    };
  }
  static integer(header: string) {
    return (s: string) => {
      const n = Number(s);
      if (!Number.isInteger(n)) throw `parsing error for ${header}: ${s} needs to be a integer`;
      return n;
    };
  }

  static satChannel() {
    return (s: string) => {
      if (s === '' || s === 'N/A') return;
      const n = Number(s);
      if (!Number.isInteger(n))
        throw `parsing error for satChannel: ${s} needs to be a integer, N/A, ''`;
      return n;
    };
  }

  static optionalNumber(header: string) {
    return (s: string) => {
      if (s === '') return;
      return this.number(header)(s);
    };
  }
  static number(header: string) {
    return (s: string) => {
      s = s.replace(',', '.');
      const n = Number(s);
      if (Number.isNaN(n)) throw `parsing error for ${header}: ${s} needs to be a number`;
      return n;
    };
  }

  static date(header: string) {
    return (s: string) => {
      if (
        s.length !== 19 ||
        s[4] !== '-' ||
        s[7] !== '-' ||
        s[10] !== ' ' ||
        s[13] !== ':' ||
        s[16] !== ':'
      ) {
        throw `parsing error for ${header}: ${s} needs to be in the format yyyy-mm-dd hh:mm:ss`;
      }
      const isoString = s.replace(' ', 'T') + 'Z';
      if (isNaN(new Date(isoString).getTime()))
        throw `parsing error for ${header}: ${s} is not a valid date`;
      return isoString;
    };
  }

  static gpsXDate(dateHeader: string, timeHeader: string) {
    return (s: string, s2: string) => {
      const time = this.gpsXTime(timeHeader)(s2);
      const { format, day, month, year } = this.gpsXDateParts(dateHeader, s);
      const isoString = `${year}-${month}-${day}T${time}Z`;
      if (format !== 'DD/MM/YYYY or MM/DD/YYYY') {
        if (isNaN(new Date(isoString).getTime()))
          throw `parsing error for ${dateHeader}, ${timeHeader}: ${s} and ${s2} do not form a valid date`;
        return isoString;
      }
      const MM_DD_IsoString = `${year}-${day}-${month}T${time}Z`;
      if (isNaN(new Date(isoString).getTime()) && isNaN(new Date(MM_DD_IsoString).getTime()))
        throw `parsing error for ${dateHeader}, ${timeHeader}: ${s} and ${s2} do not form a valid date`;
      return `${isoString} or ${MM_DD_IsoString}`;
    };
  }

  private static gpsXDateParts(dateHeader: string, s: string) {
    const monthNameMatch = /^(\d{1,2})([-./])([A-Za-z]{3})\2(\d{4}|\d{2})$/.exec(s);
    if (monthNameMatch) {
      const [, d, , monthName, y] = monthNameMatch;
      const month = this.monthDigits(monthName);
      if (!month) throw `parsing error for ${dateHeader}: ${monthName} is not a valid month`;
      return {
        format: 'DD-MMM-YY' as const,
        day: d.padStart(2, '0'),
        month,
        year: y.length === 2 ? `20${y}` : y,
      };
    }
    const numericMatch = /^(\d{1,4})([-./])(\d{1,2})\2(\d{1,4})$/.exec(s);
    if (!numericMatch)
      throw `parsing error for ${dateHeader}: ${s} needs to be of format DD-MMM-YY, DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD or with other separator`;
    if (numericMatch[1].length === 4) {
      const [, year, , month, day] = numericMatch;
      return {
        format: 'YYYY-MM-DD' as const,
        day: day.padStart(2, '0'),
        month: month.padStart(2, '0'),
        year,
      };
    }
    if (numericMatch[4].length !== 4)
      throw `parsing error for ${dateHeader}: ${s} needs to be of format DD-MMM-YY, DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD or with other separator`;
    const [, day, , month, year] = numericMatch;
    return {
      format: 'DD/MM/YYYY or MM/DD/YYYY' as const,
      day: day.padStart(2, '0'),
      month: month.padStart(2, '0'),
      year,
    };
  }

  private static gpsXTime(timeHeader: string) {
    return (s: string) => {
      const match = /^(\d{1,2}):(\d{2}):(\d{2})(?:\s*([AaPp][Mm]))?$/.exec(s.trim());
      if (!match)
        throw `parsing error for ${timeHeader}: ${s} needs to be of format hh:mm:ss or h:mm:ss AM/PM`;
      const [, hh, mm, ss, meridiem] = match;
      let hour = Number(hh);
      if (meridiem) {
        if (hour < 1 || hour > 12)
          throw `parsing error for ${timeHeader}: ${s} is not a valid 12-hour clock time`;
        const isPm = meridiem.toLowerCase() === 'pm';
        if (hour === 12) hour = isPm ? 12 : 0;
        else if (isPm) hour += 12;
      }
      if (hour > 23 || Number(mm) > 59 || Number(ss) > 59)
        throw `parsing error for ${timeHeader}: ${s} is not a valid time`;
      return `${String(hour).padStart(2, '0')}:${mm}:${ss}`;
    };
  }

  private static monthDigits(monthString: string) {
    const monthsMap: Record<string, string> = {
      Jan: '01',
      Feb: '02',
      Mar: '03',
      Apr: '04',
      May: '05',
      Jun: '06',
      Jul: '07',
      Aug: '08',
      Sep: '09',
      Oct: '10',
      Nov: '11',
      Dec: '12',
    };
    return monthsMap[monthString];
  }

  static originCode() {
    return (s: string): webEnums.OriginCode => {
      switch (s) {
        case 'Globalstar':
          return 'GLOBALSTAR';
        case 'Iridium':
          return 'IRIDIUM';
        case 'GSM-FTP':
          return 'GSM_FTP';
        case 'GSM-SMS':
          return 'GSM_SMS';
        case 'Terminal':
          return 'TERMINAL';
        case 'Collar':
          return 'COLLAR';
        case 'IoT':
          return 'NB_IOT';
        case '':
          return undefined;
        default:
          throw `parsing error for Origin: ${s} needs to be: Globalstar, Iridium, GSM-FTP, GSM-SMS, Terminal, Collar, IoT, ''`;
      }
    };
  }

  static gpsXOriginCode() {
    return (s: string): webEnums.OriginCode => {
      switch (s) {
        case 'Globalstar':
          return 'GLOBALSTAR';
        case 'Iridium':
          return 'IRIDIUM';
        case 'GSM-FTP':
          return 'GSM_FTP';
        case 'GSM':
        case 'GSM-(6,7,8)bit':
          return 'GSM_SMS';
        case 'Terminal':
          return 'TERMINAL';
        case 'Collar':
          return 'COLLAR';
        case 'IoT':
          return 'NB_IOT';
        case '':
          return undefined;
        default:
          throw `parsing error for Origin: ${s} needs to be: Globalstar, Iridium, GSM, Terminal, Collar, ''`;
      }
    };
  }

  static fixType() {
    return (s: string): webEnums.FixType => {
      switch (s) {
        case 'No Fix':
          return 'NO_FIX';
        case '1 GPS Sat':
          return 'ONE_GPS_SAT';
        case '2 GPS Sats':
          return 'TWO_GPS_SAT';
        case '2D':
          return 'TWO_D_FIX';
        case '3D':
          return 'THREE_D_FIX_NOT_VALIDATED';
        case '3D Valid':
          return 'THREE_D_FIX_VALIDATED';
        case '':
          return undefined;
        default:
          throw `parsing error for FixType: ${s} needs to be: No Fix, 1 GPS Sat, 2 GPS Sats, 2D, 3D, 3D Valid, ''`;
      }
    };
  }

  static gpsXFixType() {
    return (s: string): webEnums.FixType => {
      switch (s) {
        case 'No Fix':
          return 'NO_FIX';
        case '1 GPS Sat':
          return 'ONE_GPS_SAT';
        case '2 GPS Sats':
          return 'TWO_GPS_SAT';
        case 'GPS-2D':
          return 'TWO_D_FIX';
        case 'GPS-3D':
          return 'THREE_D_FIX_NOT_VALIDATED';
        case 'val. GPS-3D':
          return 'THREE_D_FIX_VALIDATED';
        case '':
          return undefined;
        default:
          throw `parsing error for FixType: ${s} needs to be: No Fix, 1 GPS Sat, 2 GPS Sats, 2D, 3D, 3D Valid, ''`;
      }
    };
  }

  static fourByteHex() {
    return (hexString: string) => {
      const parts = hexString.split('.');
      if (hexString.length !== 11 || parts.length !== 4)
        throw 'parsing error for LogValue. Required format: XX.XX.XX.XX';
      const hex = parts.join('');
      const int = parseInt(hex, 16);
      if (isNaN(int)) throw 'parsing error for LogValue. Not a Number';
      return int;
    };
  }

  static logType() {
    return (s: string): webEnums.LogType => {
      switch (s) {
        case 'System Log':
          return 'SYSTEM_LOG';
        case 'Transmission Log':
          return 'TRANSMISSION_LOG';
        case '':
          return undefined;
        default:
          throw `parsing error for Log Type: ${s} needs to be: System Log, Transmission Log, ''`;
      }
    };
  }

  static mortalityStatus() {
    return (s: string): webEnums.MortalityStatus => {
      switch (s) {
        case 'N/A':
          return 'NOT_AVAILABLE';
        case 'Nothing Detected':
          return 'NOTHING_DETECTED';
        case 'Low Activity No Radius':
          return 'LOW_ACTIVITY_NO_RADIUS';
        case 'Low Activity Inside Radius':
          return 'LOW_ACTIVITY_INSIDE_RADIUS';
        case 'Low Activity Outside Radius':
          return 'LOW_ACTIVITY_OUTSIDE_RADIUS';
        case 'Mortality':
          return 'MORTALITY_NO_RADIUS';
        case 'Mortality Inside Radius':
          return 'MORTALITY_INSIDE_RADIUS';
        case 'Mortality Outside Radius':
          return 'MORTALITY_OUTSIDE_RADIUS';
        case '':
          return undefined;
        default:
          throw `parsing error for Mortality Status: ${s} needs to be: N/A, Nothing Detected, Low Activity No Radius, Low Activity Inside Radius, Low Activity Outside Radius, Mortality, Mortality Inside Radius, Mortality Outside Radius, ''`;
      }
    };
  }

  static gpsXMortalityStatus() {
    return (s: string): webEnums.MortalityStatus => {
      switch (s) {
        case 'normal':
        case 'NOTHING_DETECTED':
          return 'NOTHING_DETECTED';
        case 'Low Activity within radius':
          return 'LOW_ACTIVITY_INSIDE_RADIUS';
        case 'Low Activity out of radius':
          return 'LOW_ACTIVITY_OUTSIDE_RADIUS';
        case 'Low Activity no radius':
          return 'LOW_ACTIVITY_NO_RADIUS';
        case 'Mortality no radius':
          return 'MORTALITY_NO_RADIUS';
        case 'Mortality out of radius':
          return 'MORTALITY_OUTSIDE_RADIUS';
        case 'Mortality within radius':
          return 'MORTALITY_INSIDE_RADIUS';
        case '':
          return undefined;
        case 'N/A':
          return 'NOT_AVAILABLE';
        default:
          throw `parsing error for Mort. Status: ${s} needs to be: normal, Low Activity within radius, Low Activity out of radius, Low Activity no radius, Mortality no radius, Mortality out of radius, Mortality within radius, ''`;
      }
    };
  }

  static messageType() {
    return (s: string): webEnums.ExtendedMessageType => {
      switch (s) {
        case 'No contact':
          return 'NO_CONTACT';
        case 'Mortality':
          return 'MORTALITY';
        case 'Status':
          return 'STATUS';
        case 'Null':
          return 'NULL';
        case 'Expelled':
          return 'EXPELLED';
        case '':
          return undefined;
        default:
          throw `parsing error for messageType: ${s} needs to be: No contact, Mortality, Status, Null, Expelled, ''`;
      }
    };
  }

  static gpsXMessageType() {
    return (s: string): webEnums.ExtendedMessageType => {
      switch (s) {
        case 'No Contact Event':
          return 'NO_CONTACT';
        case 'Mortality Event':
          return 'MORTALITY';
        case 'Status Message':
          return 'STATUS';
        case 'Alarm Message':
          return 'Alarm Message' as any; //NOTE this gets parsed separately in the csv import service
        case 'Null':
        case 'NULL':
          return 'NULL';
        case 'Expelled':
        case 'No Activity Event':
          return 'EXPELLED';
        case '':
        case 'N/A':
          return undefined;
        default:
          throw `parsing error for messageType: ${s} needs to be: No Contact Event, Mortality Event, Alarm Message, Expelled, NULL, Status Message, N/A, ''`;
      }
    };
  }

  static mortalityKind() {
    return (s: string): webEnums.Status => {
      switch (s) {
        case 'Mortality':
          return 'MORTALITY';
        case 'Low Activity':
          return 'LOW_ACTIVITY';
        case 'Lateral':
          return 'LATERAL';
        case '':
          return undefined;
        default:
          throw `parsing error for mortalityKind: ${s} needs to be: Mortality, Low Activity, Lateral, ''`;
      }
    };
  }

  static gpsXMortalityKind() {
    return (s: string): webEnums.Status => {
      switch (s) {
        case 'Mortality':
          return 'MORTALITY';
        case 'Low Activity':
          return 'LOW_ACTIVITY';
        case 'Lateral':
          return 'LATERAL';
        case '':
          return undefined;
        default:
          throw `parsing error for mortalityKind: ${s} needs to be: Mortality, Low Activity, ''`;
      }
    };
  }

  static alive() {
    return (s: string) => {
      if (s === '') return;
      if (s === 'Alive') return true;
      if (s === 'Dead') return false;
      throw `parsing error for alive: ${s} needs to be: Alive, Dead, ''`;
    };
  }

  static gpsXAlive() {
    return (s: string) => {
      if (s === '' || s === 'N/A') return;
      if (s === 'True' || s === 'YES') return true;
      if (s === 'False' || s === 'NO') return false;
      throw `parsing error for Alive: ${s} needs to be: YES, NO, True, False, N/A, ''`;
    };
  }

  static received() {
    return (s: string) => {
      if (s === '') return;
      if (s === 'Received') return true;
      if (s === 'Not received') return false;
      throw `parsing error for Received: ${s} needs to be: Received, Not received, ''`;
    };
  }

  static gpsXReceived() {
    return (s: string) => {
      if (s === '' || s === 'N/A') return;
      if (s === 'True' || s === 'YES') return true;
      if (s === 'False' || s === 'NO') return false;
      throw `parsing error for Received: ${s} needs to be: YES, NO, True, False, N/A, ''`;
    };
  }

  static expelled() {
    return (s: string) => {
      if (s === '') return;
      if (s === 'Expelled') return true;
      if (s === 'Implanted') return false;
      throw `parsing error for expelled: ${s} needs to be: Expelled, Implanted, ''`;
    };
  }

  static gpsXExpelled() {
    return (s: string) => {
      if (s === '') return;
      if (s === 'expelled') return true;
      if (['implanted', 'not expelled'].includes(s)) return false;
      if (!isNaN(parseInt(s)) && parseInt(s) > 0) return false;
      throw `parsing error for Stauts: ${s} needs to be: expelled, implanted, '', a number, not expelled`;
    };
  }
}
